// src/components/attributes/attributes-table.tsx

/**
 * Attributes Table Component
 * Displays a list of attributes with actions
 */

import { Pencil, Trash2, Database, Globe, Server, User, Lock } from 'lucide-react'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import type { Attribute, AttributeSourceType } from '@/types/attribute'
import { DATA_TYPE_LABELS, SOURCE_TYPE_LABELS } from '@/types/attribute'

interface AttributesTableProps {
  attributes: Attribute[]
  isLoading?: boolean
  onEdit: (attribute: Attribute) => void
  onDelete: (attribute: Attribute) => void
}

function SourceIcon({ source }: { source: AttributeSourceType }) {
  switch (source) {
    case 'LDAP':
      return <User className="h-3.5 w-3.5" />
    case 'DATABASE':
      return <Database className="h-3.5 w-3.5" />
    case 'REST_API':
      return <Globe className="h-3.5 w-3.5" />
    case 'MANUAL':
      return <Server className="h-3.5 w-3.5" />
  }
}

function LoadingSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-4 w-32" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-24" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-16" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-8 w-20" />
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}

export function AttributesTable({
  attributes,
  isLoading = false,
  onEdit,
  onDelete,
}: AttributesTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px]">Key</TableHead>
            <TableHead>Display Name</TableHead>
            <TableHead className="w-[100px]">Data Type</TableHead>
            <TableHead className="w-[120px]">Source</TableHead>
            <TableHead className="w-[100px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <LoadingSkeleton />
          ) : attributes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center">
                No attributes found.
              </TableCell>
            </TableRow>
          ) : (
            attributes.map((attribute) => (
              <TableRow key={attribute.id} className="group">
                <TableCell className="font-mono text-sm">
                  <div className="flex items-center gap-1.5">
                    {attribute.isSystemManaged && (
                      <Lock
                        className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                        aria-label="System-managed attribute"
                      />
                    )}
                    {attribute.key}
                  </div>
                </TableCell>
                <TableCell>{attribute.displayName}</TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {DATA_TYPE_LABELS[attribute.dataType]}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <SourceIcon source={attribute.sourceType} />
                    <span className="text-sm">
                      {SOURCE_TYPE_LABELS[attribute.sourceType]}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {attribute.isSystemManaged ? (
                    <span className="text-xs text-muted-foreground">System</span>
                  ) : (
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => onEdit(attribute)}
                      >
                        <Pencil className="h-4 w-4" />
                        <span className="sr-only">Edit</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onDelete(attribute)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
